import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { seoServices } from '../../../../services/seoAPIServices';
import { useAdminStore } from '../../../../store/adminStore';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Label } from '../../../../components/ui/label';
import { CategorySearch } from '../../../../components/Admin/Category/CategorySearch';
import { Loader2, ChevronDown, Check, Search } from 'lucide-react';

// --- CONSTANTS ---
const PAGE_TYPE_DOC_MAP = {
    'BUYER_CHILD_LOCAL': 'pageService',
    'SELLER_CHILD_LOCAL': 'pageService',
    // Fallbacks if you expand the list
    'BUYER_CHILD_NATIONAL': 'pageService',
    'SELLER_CHILD_NATIONAL': 'pageService',
};

const SeoBulkGenerator = ({ open, onOpenChange, audience }) => {
  const countries = useAdminStore((state) => state.countries);
  
  // -- UI State --
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  
  // -- Source Page Search State --
  const [childPages, setChildPages] = useState([]);
  const [isPagesLoading, setIsPagesLoading] = useState(false);
  const [sourceQuery, setSourceQuery] = useState('');
  const [showSourceList, setShowSourceList] = useState(false);
  const sourceListRef = useRef(null);

  // -- Sanity Search State --
  const [sanityDocs, setSanityDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [sanitySearch, setSanitySearch] = useState('');
  const [isSanityDropdownOpen, setIsSanityDropdownOpen] = useState(false);

  // Default Page Type based on Audience
  const defaultPageType = audience === 'BUYER' ? 'BUYER_CHILD_LOCAL' : 'SELLER_CHILD_LOCAL';

  const { register, control, handleSubmit, watch, setValue, reset } = useForm({
      defaultValues: {
          country_code: 'IN',
          category_type: 'PARENT',
          page_type: defaultPageType,
          dry_run: true,
          sanity_doc_id: ''
      }
  });

  const watchedCategory = watch('category_id');
  const watchedCatType = watch('category_type'); 
  const watchedCountry = watch('country_code');
  const watchedPageType = watch('page_type');
  const watchedSanityId = watch('sanity_doc_id');

  // --- 1. Init / Reset ---
  useEffect(() => {
    if (open) {
        setResult(null);
        setSanitySearch('');
        // We don't full reset form to keep defaults, just clear result
    }
  }, [open]);

  // --- 2. Fetch Sanity Docs (Dynamic) ---
  useEffect(() => {
    if (!open) return;
    
    // Determine doc type needed (defaulting to 'pageService' for local pages)
    const targetDocType = PAGE_TYPE_DOC_MAP[watchedPageType] || 'pageService';

    const fetchDocs = async () => {
        setLoadingDocs(true);
        try {
            const docs = await seoServices.getSanityDocs(targetDocType);
            setSanityDocs(docs);
        } catch (error) {
            console.error("Failed to load sanity docs", error);
        } finally {
            setLoadingDocs(false);
        }
    };

    fetchDocs();
  }, [watchedPageType, open]);

  // Filter Sanity Docs
  const filteredDocs = useMemo(() => {
     if(!sanitySearch) return sanityDocs.slice(0, 10);
     return sanityDocs.filter(d => 
         (d.title?.toLowerCase() || '').includes(sanitySearch.toLowerCase()) || 
         (d.id && d.id.includes(sanitySearch))
     ).slice(0, 10);
  }, [sanityDocs, sanitySearch]);

  const selectedDoc = sanityDocs.find(d => d.id === watchedSanityId);


  // --- 3. Click Outside Handler for Dropdowns ---
  useEffect(() => {
    function handleClickOutside(event) {
      if (sourceListRef.current && !sourceListRef.current.contains(event.target)) {
        setShowSourceList(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- 4. Fetch Source Pages (for Child Mode) ---
  useEffect(() => {
    const fetchChildren = async () => {
        if (watchedCatType === 'CHILD' && watchedCountry) {
            setIsPagesLoading(true);
            try {
                const params = {
                    country_code: watchedCountry,
                    // Fetch the "National" version to clone down to "Local"
                    page_type: audience === 'BUYER' ? 'BUYER_CHILD_NATIONAL' : 'SELLER_CHILD_NATIONAL',
                    limit: 1000 
                };
                if (watchedCategory) params.category_id = watchedCategory;

                const res = await seoServices.listPages(params);
                setChildPages(res.items);
            } catch (e) {
                console.error("Failed to load child pages", e);
            } finally {
                setIsPagesLoading(false);
            }
        }
    };
    if(watchedCatType === 'CHILD') fetchChildren();
  }, [watchedCatType, watchedCategory, watchedCountry, audience]);

  // Filter Source Pages locally
  const filteredPages = childPages.filter(p => 
      p.slug.toLowerCase().includes(sourceQuery.toLowerCase()) ||
      (p.keyword && p.keyword.toLowerCase().includes(sourceQuery.toLowerCase()))
  );

  // --- 5. Submit ---
  const onSubmit = async (data) => {
    // Validation
    if (data.category_type === 'PARENT' && !data.sanity_doc_id) {
        alert("Sanity Template is mandatory for Standard Generation.");
        return;
    }

    setLoading(true);
    setResult(null);
    try {
      const stateCodesList = data.state_codes_input 
        ? data.state_codes_input.split(',').map(s => s.trim().toUpperCase()).filter(Boolean) 
        : null;

      const payload = {
         country_code: data.country_code,
         audience,
         page_type: data.page_type,
         dry_run: data.dry_run,
         state_codes: stateCodesList, 
         min_population: data.min_population ? parseInt(data.min_population) : null,
         limit_top_cities: data.limit_top_cities ? parseInt(data.limit_top_cities) : null
      };

      if (data.category_type === 'PARENT') {
          payload.category_id = data.category_id;
          payload.sanity_doc_id = data.sanity_doc_id;
      } else {
          payload.source_page_id = parseInt(data.source_page_id);
          // Optional override for Child mode
          if (data.sanity_doc_id) payload.sanity_doc_id = data.sanity_doc_id;
      }

      const res = await seoServices.bulkGenerate(payload);
      setResult(res);

    } catch (err) {
      let errorMessage = err.message || "An unknown error occurred";
      const detail = err.response?.data?.detail;

      if (detail) {
          if (Array.isArray(detail)) {
              errorMessage = detail.map(e => `${e.loc ? e.loc.join('.') + ': ' : ''}${e.msg}`).join(' | ');
          } else if (typeof detail === 'object') {
              errorMessage = JSON.stringify(detail);
          } else {
              errorMessage = detail;
          }
      }
      setResult({ error: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Generate {audience} Local Pages</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
           
           {/* Global Settings */}
           <div className="grid grid-cols-2 gap-4">
              <div>
                 <Label>Country</Label>
                 <Controller 
                    name="country_code" 
                    control={control} 
                    render={({field}) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {countries.map(c => <SelectItem key={c.country_code} value={c.country_code}>{c.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    )}
                 />
              </div>
              <div>
                  <Label>Generation Source</Label>
                  <Controller 
                    name="category_type" 
                    control={control} 
                    render={({field}) => (
                        <Select value={field.value} onValueChange={(val) => {
                            field.onChange(val);
                            setValue('sanity_doc_id', ''); // Reset doc on mode change
                        }}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="PARENT">From Category (Standard)</SelectItem>
                                <SelectItem value="CHILD">From Existing National Page</SelectItem>
                            </SelectContent>
                        </Select>
                    )}
                 />
              </div>
           </div>

           {/* --- MODE: PARENT (Standard) --- */}
           {watchedCatType === 'PARENT' ? (
               <div className="space-y-3 p-3 border rounded bg-gray-50">
                  <div>
                    <Label>Select Category</Label>
                    <CategorySearch 
                        locale={watchedCountry || 'IN'} 
                        onSelect={(c) => setValue('category_id', c.id)} 
                    />
                  </div>
                  
                  {/* Sanity Dropdown (Mandatory) */}
                  <div className="relative">
                     <Label>Sanity Template ID <span className="text-red-500">*</span></Label>
                     <div className="relative">
                        <div 
                            className={`flex items-center justify-between border rounded p-2 bg-white cursor-pointer h-10 text-sm ${!watchedSanityId ? 'border-red-200' : ''}`}
                            onClick={() => setIsSanityDropdownOpen(!isSanityDropdownOpen)}
                        >
                            {selectedDoc ? (
                                <span className="truncate w-[90%] font-medium">{selectedDoc.title}</span>
                            ) : (
                                <span className="text-gray-500">Select Template...</span>
                            )}
                            <Search className="w-4 h-4 text-gray-400" />
                        </div>

                        {isSanityDropdownOpen && (
                            <div className="absolute z-50 w-full bg-white border rounded shadow-lg mt-1 p-2">
                                <div className="flex items-center border-b pb-2 mb-2">
                                    <Search className="w-4 h-4 text-gray-400 mr-2"/>
                                    <input 
                                        className="outline-none text-sm w-full"
                                        placeholder="Search title..."
                                        value={sanitySearch}
                                        onChange={(e) => setSanitySearch(e.target.value)}
                                        autoFocus
                                    />
                                </div>
                                <ul className="max-h-40 overflow-auto space-y-1">
                                    {loadingDocs ? (
                                        <li className="text-center py-2"><Loader2 className="w-4 h-4 animate-spin mx-auto"/></li>
                                    ) : filteredDocs.map(doc => (
                                        <li 
                                            key={doc.id} 
                                            onClick={() => {
                                                setValue('sanity_doc_id', doc.id); 
                                                setIsSanityDropdownOpen(false);
                                            }}
                                            className="text-xs p-2 hover:bg-gray-100 cursor-pointer rounded flex justify-between group"
                                        >
                                            <span className="truncate max-w-[200px]">{doc.title || 'Untitled'}</span>
                                            <span className={`px-1 rounded text-[10px] ${doc.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                                {doc.status}
                                            </span>
                                        </li>
                                    ))}
                                    {!loadingDocs && filteredDocs.length === 0 && <li className="text-xs text-gray-400 p-2">No docs found</li>}
                                </ul>
                            </div>
                        )}
                     </div>
                  </div>
               </div>
           ) : (
               /* --- MODE: CHILD (From Source) --- */
               <div className="space-y-3 p-3 border rounded bg-blue-50 border-blue-100">
                   
                   {/* Source Page Search */}
                   <div ref={sourceListRef} className="relative">
                       <Label>Select Source Page (National)</Label>
                       <Controller 
                            name="source_page_id"
                            control={control}
                            render={({field}) => {
                                const selectedPage = childPages.find(p => String(p.id) === String(field.value));
                                return (
                                    <>
                                        <div className="relative">
                                            <Input 
                                                placeholder={isPagesLoading ? "Loading pages..." : "Search existing pages..."}
                                                value={showSourceList ? sourceQuery : (selectedPage ? selectedPage.slug : sourceQuery)}
                                                onChange={(e) => {
                                                    setSourceQuery(e.target.value);
                                                    setShowSourceList(true);
                                                }}
                                                onFocus={() => {
                                                    setSourceQuery(''); 
                                                    setShowSourceList(true);
                                                }}
                                                className="pr-8 bg-white"
                                                disabled={isPagesLoading}
                                            />
                                            <ChevronDown className="absolute right-2 top-2.5 h-4 w-4 text-gray-400 pointer-events-none"/>
                                        </div>

                                        {showSourceList && (
                                            <div className="absolute z-50 w-full bg-white border rounded shadow-lg mt-1 max-h-60 overflow-y-auto">
                                                {filteredPages.length > 0 ? (
                                                    filteredPages.map(p => (
                                                        <div 
                                                            key={p.id} 
                                                            onClick={() => {
                                                                field.onChange(p.id);
                                                                setShowSourceList(false);
                                                                setSourceQuery('');
                                                            }}
                                                            className={`p-2 hover:bg-blue-50 cursor-pointer text-xs flex flex-col border-b last:border-none ${String(field.value) === String(p.id) ? 'bg-blue-50' : ''}`}
                                                        >
                                                            <div className="flex justify-between">
                                                                <span className="font-medium text-gray-800">{p.slug}</span>
                                                                {String(field.value) === String(p.id) && <Check className="w-3 h-3 text-blue-600"/>}
                                                            </div>
                                                            {p.keyword && <span className="text-blue-600 text-[10px]">KW: {p.keyword}</span>}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="p-3 text-center text-gray-500 text-xs">No pages found.</div>
                                                )}
                                            </div>
                                        )}
                                    </>
                                );
                            }}
                       />
                       <p className="text-[10px] text-blue-600 mt-1">* New pages will inherit Category & Keyword.</p>
                   </div>

                   {/* Sanity Dropdown (Optional Override) */}
                   <div className="relative">
                     <Label>Sanity Template Override (Optional)</Label>
                     <div className="relative">
                        <div 
                            className="flex items-center justify-between border rounded p-2 bg-white cursor-pointer h-10 text-sm"
                            onClick={() => setIsSanityDropdownOpen(!isSanityDropdownOpen)}
                        >
                            {selectedDoc ? (
                                <span className="truncate w-[90%] font-medium">{selectedDoc.title}</span>
                            ) : (
                                <span className="text-gray-400 italic">Inherit from Source...</span>
                            )}
                            <Search className="w-4 h-4 text-gray-400" />
                        </div>

                        {isSanityDropdownOpen && (
                            <div className="absolute z-50 w-full bg-white border rounded shadow-lg mt-1 p-2">
                                <div className="flex items-center border-b pb-2 mb-2">
                                    <Search className="w-4 h-4 text-gray-400 mr-2"/>
                                    <input 
                                        className="outline-none text-sm w-full"
                                        placeholder="Search title..."
                                        value={sanitySearch}
                                        onChange={(e) => setSanitySearch(e.target.value)}
                                        autoFocus
                                    />
                                </div>
                                <ul className="max-h-40 overflow-auto space-y-1">
                                    {loadingDocs ? (
                                        <li className="text-center py-2"><Loader2 className="w-4 h-4 animate-spin mx-auto"/></li>
                                    ) : filteredDocs.map(doc => (
                                        <li 
                                            key={doc.id} 
                                            onClick={() => {
                                                setValue('sanity_doc_id', doc.id); 
                                                setIsSanityDropdownOpen(false);
                                            }}
                                            className="text-xs p-2 hover:bg-gray-100 cursor-pointer rounded flex justify-between group"
                                        >
                                            <span className="truncate max-w-[200px]">{doc.title || 'Untitled'}</span>
                                            <span className={`px-1 rounded text-[10px] ${doc.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                                {doc.status}
                                            </span>
                                        </li>
                                    ))}
                                    {!loadingDocs && filteredDocs.length === 0 && <li className="text-xs text-gray-400 p-2">No docs found</li>}
                                </ul>
                            </div>
                        )}
                     </div>
                  </div>
               </div>
           )}

           <div>
               <Label>Target Page Type</Label>
               <Controller 
                  name="page_type"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value={defaultPageType}>Standard Local Page ({defaultPageType})</SelectItem>
                        </SelectContent>
                    </Select>
                  )}
               />
           </div>

           <div className="bg-gray-50 p-3 rounded space-y-3 border">
               <p className="text-xs font-bold text-gray-600 uppercase">Targeting Filters</p>
               
               <div>
                   <Label className="text-xs">State Codes (Comma Separated)</Label>
                   <Input 
                        {...register('state_codes_input')} 
                        placeholder="e.g. MH, GJ, KA, GA" 
                        className="text-xs bg-white"
                   />
               </div>

               <div className="grid grid-cols-3 gap-3">
                    <div>
                        <Label className="text-[10px]">Min Population</Label>
                        <Input type="number" {...register('min_population')} placeholder="10000" className="h-8 text-xs bg-white" />
                    </div>
                    <div className="col-span-2">
                        <Label className="text-[10px]">Top Cities Limit</Label>
                        <Controller 
                            name="limit_top_cities"
                            control={control}
                            render={({field}) => (
                                <Select onValueChange={field.onChange}>
                                    <SelectTrigger className="h-8 text-xs bg-white"><SelectValue placeholder="All Cities" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="10">Top 10 Cities</SelectItem>
                                        <SelectItem value="20">Top 20 Cities</SelectItem>
                                        <SelectItem value="50">Top 50 Cities</SelectItem>
                                        <SelectItem value="100">Top 100 Cities</SelectItem>
                                    </SelectContent>
                                </Select>
                            )}
                        />
                    </div>
               </div>
           </div>

           <div className="flex items-center gap-2 bg-yellow-50 p-2 border border-yellow-200 rounded">
              <input type="checkbox" {...register('dry_run')} className="rounded text-yellow-600" />
              <Label>Dry Run (Test only)</Label>
           </div>

           <DialogFooter>
              <Button type="submit" disabled={loading}>{loading ? "Processing..." : "Generate"}</Button>
           </DialogFooter>
        </form>

        {result && (
             <div className={`mt-4 p-3 rounded text-sm border ${result.error ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                {result.error ? (
                     <p className="text-red-700 font-medium">{result.error}</p>
                ) : (
                    <div className="text-green-800 space-y-1">
                        <p className="font-bold">{result.message}</p>
                        <p>Created: {result.pages_created} | Skipped: {result.pages_skipped_existing}</p>
                        <div className="text-xs font-mono mt-2 bg-white p-2 rounded border max-h-20 overflow-y-auto">
                            {result.sample_slugs?.map(s => <div key={s}>{s}</div>)}
                        </div>
                    </div>
                )}
             </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SeoBulkGenerator;